import { useMemo } from 'react'

import { DownOutlined, UpOutlined } from '@ant-design/icons'
import { useBoolean, useRequest } from 'ahooks'
import { Button, Checkbox, CheckboxOptionType, CheckboxProps } from 'antd'
import { createStyles } from 'antd-style'

import { toArray } from '../../utils/array'
import { DataError } from '../DataError'

import { CheckboxGroupCore } from './CheckboxGroupCore'
import { CheckboxGroupCoreProps } from './CheckboxGroupCore/interface'
import { CheckboxValue, RemoteCheckboxGroupProps } from './interface'

const useStyles = createStyles(({ prefixCls, token }) => ({
  wrapper: {
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'flex-start'
  },
  checkboxGroups: {
    flex: 1,
    display: 'grid',
    gap: token.paddingXS,
    gridTemplateColumns: 'repeat(10,minmax(90px,1fr))',
    maxHeight: 32,
    overflow: 'hidden',
    [`&.show-more`]: {
      maxHeight: 320
    },
    [`.${prefixCls}-checkbox-wrapper`]: {
      height: 32,
      lineHeight: '32px'
    }
  },
  checkbox: {
    [`.${prefixCls}-checkbox+span`]: {
      overflow: 'hidden',
      whiteSpace: 'nowrap'
    }
  },
  expandButton: {
    flex: 'none',
    padding: '0 !important'
  },
  expandButtonHidden: {
    visibility: 'hidden'
  }
}))

export const RemoteCheckboxGroup = <
  T,
  P,
  M extends boolean = true,
  V = M extends true ? CheckboxValue[] : CheckboxValue
>({
  api,
  readyRequest,
  requestParams,
  refreshDeps,
  resolveOptions,

  multiple,
  value,
  onChange,

  hideCheckAll = false
}: RemoteCheckboxGroupProps<T, P, M, V>) => {
  const [showMore, toggleShowMore] = useBoolean(false)

  const {
    data = [],
    error,
    loading,
    refreshAsync
  } = useRequest(() => api(requestParams), {
    manual: false,
    ready: readyRequest,
    refreshDeps
  })

  const options = useMemo(
    () => resolveOptions?.(data) ?? (data as CheckboxOptionType[]),
    [data, resolveOptions]
  )

  const handleAllCheckboxChange: CheckboxProps['onChange'] = e => {
    const checked = e.target.checked
    const value = checked ? options.map(item => item.value) : []
    onChange?.(value as V, data)
  }

  const handleCoreChecobixGroupChange: CheckboxGroupCoreProps['onChange'] =
    value => {
      onChange?.(multiple ? (value as V) : (value?.[0] as V), data)
    }

  const showAllCheckbox = !!multiple && !!options.length && !hideCheckAll

  const { cx, styles } = useStyles()

  return error ? (
    <DataError error={error} loading={loading} reload={refreshAsync} />
  ) : (
    <div className={styles.wrapper}>
      <div
        className={cx(styles.checkboxGroups, {
          ['show-more']: showMore
        })}
      >
        {showAllCheckbox && (
          <Checkbox
            checked={(value as V[]).length === options.length}
            className={styles.checkbox}
            onChange={handleAllCheckboxChange}
          >
            全部
          </Checkbox>
        )}
        <CheckboxGroupCore
          className={styles.checkbox}
          options={options}
          value={toArray(value as CheckboxValue)}
          onChange={handleCoreChecobixGroupChange}
        />
      </div>

      <Button
        type='link'
        className={cx(styles.expandButton, {
          [styles.expandButtonHidden]: options.length <= 10
        })}
        onClick={toggleShowMore.toggle}
      >
        {showMore ? (
          <span>
            收起&nbsp;
            <UpOutlined />
          </span>
        ) : (
          <span>
            展开&nbsp;
            <DownOutlined />
          </span>
        )}
      </Button>
    </div>
  )
}
